import React, { useState } from 'react';
import { toast } from 'sonner';
import { Wallet } from 'lucide-react';
import apiClient from '../utils/apiClient';

/**
 * "Credit on account" + the action that returns it.
 *
 * A customer's `credit_balance` appeared NOWHERE in the app. It arrives from
 * four directions — an overpayment, a transfer downgrade, a move-out, or an
 * operator adjustment — and the endpoint to pay it back
 * (POST /payments/refund-credit/{customer_id}) had no caller at all. So the
 * closing order the operator actually works to — move out, refund what is
 * owed back, archive the customer — had no middle step: nothing showed the
 * credit, and nothing could return it.
 *
 * Renders only when there IS a credit, so it is a signal rather than another
 * always-on zero.
 */
const CustomerCreditBadge = ({ facilityId, customerId, credit, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const available = Number(credit || 0);
  if (!facilityId || !customerId || available <= 0) return null;

  const submit = async () => {
    const value = parseFloat(amount);
    if (!value || value <= 0) { toast.error('Enter an amount.'); return; }
    if (value > available) {
      // The server enforces this too; failing here saves a round trip and
      // says which number is the ceiling.
      toast.error(`Only $${available.toFixed(2)} is available to refund.`);
      return;
    }
    if (!reason || reason.trim().length < 3) {
      toast.error('A reason is required.'); return;
    }
    setBusy(true);
    try {
      await apiClient.post(
        `/api/facilities/${facilityId}/payments/refund-credit/${customerId}`,
        { amount: value, reason: reason.trim() },
      );
      toast.success(`Refunded $${value.toFixed(2)}.`);
      setOpen(false);
      setAmount('');
      setReason('');
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Refund failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-emerald-200 bg-emerald-50">
        <Wallet className="h-3.5 w-3.5 text-emerald-600" />
        <span className="text-[13px] text-emerald-900">
          Credit on account{' '}
          <span className="font-semibold">${available.toFixed(2)}</span>
        </span>
        <button
          type="button"
          onClick={() => { setAmount(available.toFixed(2)); setOpen(true); }}
          className="text-[12px] font-medium text-emerald-700 hover:text-emerald-900 underline"
        >
          Refund
        </button>
      </div>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4"
          onClick={() => setOpen(false)}
        >
          <div
            className="bg-white rounded-xl shadow-xl max-w-sm w-full p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-[14px] font-semibold text-slate-900 mb-1">
              Refund credit
            </h3>
            <p className="text-[12px] text-slate-500 mb-3">
              Returns money already owed to this customer. Record how it was
              actually paid — the platform does not move the money, it records
              that you did. ${available.toFixed(2)} available.
            </p>
            <label className="block text-[12px] font-medium text-slate-600 mb-1">
              Amount
            </label>
            <input
              type="number" min="0" step="0.01" max={available}
              className="w-full border border-slate-300 rounded-md px-3 py-1.5 text-[13px] mb-3"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
            <label className="block text-[12px] font-medium text-slate-600 mb-1">
              Reason
            </label>
            <input
              type="text"
              placeholder="e.g. move-out credit returned by check #1042"
              className="w-full border border-slate-300 rounded-md px-3 py-1.5 text-[13px] mb-3"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
            <div className="flex justify-end gap-2">
              <button
                type="button" onClick={() => setOpen(false)}
                className="px-3.5 py-2 rounded-lg border border-slate-300 bg-slate-200 text-[13px] font-medium text-slate-700 hover:bg-slate-300"
              >
                Cancel
              </button>
              <button
                type="button" onClick={submit} disabled={busy}
                className="px-3.5 py-2 rounded-lg bg-emerald-600 text-white text-[13px] font-medium hover:bg-emerald-700 disabled:opacity-50"
              >
                {busy ? 'Refunding…' : 'Refund'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CustomerCreditBadge;
